///
/// EarthBrowser
///
/// Created by Matt Giger on September 19, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// Trial abuse analytics API. Row shapes are snake_case as returned by the
/// server (raw postgres rows pass through unmapped).
///

import { authenticatedApi } from './authenticatedApi'

export interface TrialAbuseSummary {
  totalAttempts: number
  successfulRegistrations: number
  blockedAttempts: number
  successRate: number
  blockRate: number
  highRiskPatterns: number
  uniqueIPs: number
  uniqueDevices: number
}

export interface AbusePatternRow {
  pattern_type: string
  identifier: string
  identifier_type: 'ip' | 'device'
  occurrence_count: number
  risk_score: number
  last_seen_at: string
}

export interface BlockedRegistrationRow {
  email: string
  ip_address: string
  device_fingerprint: string | null
  block_reason: string
  created_at: string
}

export interface RegistrationStatsRow {
  registration_date: string
  total_attempts: number
  successful: number
  blocked: number
  success_rate: number
  block_rate: number
  unique_ips: number
  unique_devices: number
  unique_emails: number
}

export interface RegistrationAttemptRow {
  email: string
  ip_address?: string
  device_fingerprint?: string | null
  user_agent: string
  success: boolean
  blocked: boolean
  block_reason: string | null
  failure_reason: string | null
  created_at: string
  user_id: number | null
}

export interface AbuseUserRow {
  id: number
  username: string | null
  email: string
  status: string
  account_type: string
  trial_started_at: string | null
  trial_ends_at: string | null
  trial_converted: boolean
  created_at: string
}

export const trialAbuseApi = authenticatedApi.injectEndpoints({
  endpoints: (builder) => ({
    getTrialAbuseAnalytics: builder.query<{
      summary: TrialAbuseSummary
      highRiskPatterns: AbusePatternRow[]
      recentBlocked: BlockedRegistrationRow[]
      registrationStats: RegistrationStatsRow[]
    }, { days?: number }>({
      query: ({ days = 30 }) => `/api/admin/trial-abuse-analytics?days=${days}`,
      providesTags: ['TrialAbuse'],
    }),

    getTrialAbuseDetails: builder.query<{
      pattern: AbusePatternRow | null
      attempts: RegistrationAttemptRow[]
      users: AbuseUserRow[]
    }, { identifier: string; type: 'ip' | 'device' }>({
      query: ({ identifier, type }) =>
        `/api/admin/trial-abuse-details?identifier=${encodeURIComponent(identifier)}&type=${type}`,
      providesTags: ['TrialAbuse'],
    }),
  }),
})

export const {
  useGetTrialAbuseAnalyticsQuery,
  useGetTrialAbuseDetailsQuery,
} = trialAbuseApi
